import React, { useEffect, useRef, useState, useMemo } from 'react';
import './index.scss';
import { request } from '@/fetch/core';
import { Method } from '@/fetch/core/util';
import { HttpStatusCode } from '@/fetch/core/constant';
import { message } from 'antd';
import {
  cloudFetchConfig,
  getClstag,
  analyzeLog,
  debounceImmediate,
} from '@/utils/util';
import {
  addGlobalEventListener,
  removeGlobalEventListener,
} from '@/utils/emit';
import { DriveModeKey, EventName } from '@/utils/constant';
import { RootState } from '@/redux/store';
import { useSelector } from 'react-redux';
import {
  getNextHigherSpeedLimit,
  getNextLowerSpeedLimit,
  toNumericSpeedLimit,
} from './speedLimitStep';

const enum speedLimitEnum {
  NO_Limit = 'noLimit',
  TEN = 'TEN',
  NINE = 'NINE',
  EIGHT = 'EIGHT',
  SEVEN = 'SEVEN',
  SIX = 'SIX',
  FIVE = 'FIVE',
  FOUR = 'FOUR',
  THREE = 'THREE',
  TWO = 'TWO',
  ONE = 'ONE',
}
const enumFindNumber = new Map<speedLimitEnum, number>([
  [speedLimitEnum.TEN, 50],
  [speedLimitEnum.NINE, 45],
  [speedLimitEnum.EIGHT, 40],
  [speedLimitEnum.SEVEN, 35],
  [speedLimitEnum.SIX, 30],
  [speedLimitEnum.FIVE, 25],
  [speedLimitEnum.FOUR, 20],
  [speedLimitEnum.THREE, 15],
  [speedLimitEnum.TWO, 10],
  [speedLimitEnum.ONE, 5],
  [speedLimitEnum.NO_Limit, 80],
]);

interface SpeedLimitInfo {
  type: speedLimitEnum;
  num: number;
  default: string;
  active: string;
  clstag: string | undefined;
}

const SpeedControl = (props: {
  vehicleName: string;
  cockpitNumber: string;
  takeOverBySelf: boolean;
  userName: string;
  LC_BUTTON_TRIANGLE: boolean;
  LC_BUTTON_CROSS: boolean;
}) => {
  const {
    takeOverBySelf,
    LC_BUTTON_TRIANGLE,
    LC_BUTTON_CROSS,
    vehicleName,
    cockpitNumber,
    userName,
  } = props;

  const maxVelocity = useSelector(
    (state: RootState) => state.cockpitRealtime?.maxVelocity
  );
  const drivingModeKey = useSelector(
    (state: RootState) => state.cockpitRealtime?.drivingModeKey
  );
  const gearPosition = useSelector(
    (state: RootState) => state.cockpitRealtime?.gearPosition
  );
  const cockpitMode = useSelector(
    (state: RootState) => state.newCommon?.cockpitMode
  );

  const [curSpeedLimit, setCurSpeedLimit] = useState<any>(null);
  const [initSpeed, setInitSpeed] = useState<any>(null);

  // 存储D挡记忆限速
  const dGearSpeedLimitRef = useRef<any>(null);

  const speedLimitInfo: SpeedLimitInfo[] = useMemo(
    () => [
      {
        type: speedLimitEnum.TEN,
        num: enumFindNumber.get(speedLimitEnum.TEN)!,
        default: require('@/assets/images/monitor/speed-one-default.png'),
        active: require('@/assets/images/monitor/speed-one-active.png'),
        clstag: getClstag(cockpitMode, 'SinglePage_ChangeSpeed_TEN'),
      },
      {
        type: speedLimitEnum.NINE,
        num: enumFindNumber.get(speedLimitEnum.NINE)!,
        default: require('@/assets/images/monitor/speed-two-default.png'),
        active: require('@/assets/images/monitor/speed-two-active.png'),
        clstag: getClstag(cockpitMode, 'SinglePage_ChangeSpeed_NINE'),
      },
      {
        type: speedLimitEnum.EIGHT,
        num: enumFindNumber.get(speedLimitEnum.EIGHT)!,
        default: require('@/assets/images/monitor/speed-three-default.png'),
        active: require('@/assets/images/monitor/speed-three-active.png'),
        clstag: getClstag(cockpitMode, 'SinglePage_ChangeSpeed_EIGHT'),
      },
      {
        type: speedLimitEnum.SEVEN,
        num: enumFindNumber.get(speedLimitEnum.SEVEN)!,
        default: require('@/assets/images/monitor/speed-four-default.png'),
        active: require('@/assets/images/monitor/speed-four-active.png'),
        clstag: getClstag(cockpitMode, 'SinglePage_ChangeSpeed_SEVEN'),
      },
      {
        type: speedLimitEnum.SIX,
        num: enumFindNumber.get(speedLimitEnum.SIX)!,
        default: require('@/assets/images/monitor/speed-five-default.png'),
        active: require('@/assets/images/monitor/speed-five-active.png'),
        clstag: getClstag(cockpitMode, 'SinglePage_ChangeSpeed_SIX'),
      },
      {
        type: speedLimitEnum.FIVE,
        num: enumFindNumber.get(speedLimitEnum.FIVE)!,
        default: require('@/assets/images/monitor/speed-six-default.png'),
        active: require('@/assets/images/monitor/speed-six-active.png'),
        clstag: getClstag(cockpitMode, 'SinglePage_ChangeSpeed_FIVE'),
      },
      {
        type: speedLimitEnum.FOUR,
        num: enumFindNumber.get(speedLimitEnum.FOUR)!,
        default: require('@/assets/images/monitor/speed-seven-default.png'),
        active: require('@/assets/images/monitor/speed-seven-active.png'),
        clstag: getClstag(cockpitMode, 'SinglePage_ChangeSpeed_FOUR'),
      },
      {
        type: speedLimitEnum.THREE,
        num: enumFindNumber.get(speedLimitEnum.THREE)!,
        default: require('@/assets/images/monitor/speed-eight-default.png'),
        active: require('@/assets/images/monitor/speed-eight-active.png'),
        clstag: getClstag(cockpitMode, 'SinglePage_ChangeSpeed_THREE'),
      },
      {
        type: speedLimitEnum.TWO,
        num: enumFindNumber.get(speedLimitEnum.TWO)!,
        default: require('@/assets/images/monitor/speed-nine-default.png'),
        active: require('@/assets/images/monitor/speed-nine-active.png'),
        clstag: getClstag(cockpitMode, 'SinglePage_ChangeSpeed_TWO'),
      },
      {
        type: speedLimitEnum.ONE,
        num: enumFindNumber.get(speedLimitEnum.ONE)!,
        default: require('@/assets/images/monitor/speed-ten-default.png'),
        active: require('@/assets/images/monitor/speed-ten-active.png'),
        clstag: getClstag(cockpitMode, 'SinglePage_ChangeSpeed_ONE'),
      },
    ],
    [cockpitMode]
  );

  useEffect(() => {
    const defaultSpeed =
      takeOverBySelf && maxVelocity ? maxVelocity : initSpeed;
    setCurSpeedLimit(defaultSpeed);
  }, [takeOverBySelf, maxVelocity, initSpeed]);

  useEffect(() => {
    if (!props.userName) {
      return;
    }
    getUserConfig(); // 获取最大限速的默认设置
  }, [props.userName]);

  useEffect(() => {
    if (takeOverBySelf) {
      if (gearPosition === 'R') {
        changeSpeedLimit(5, false);
      } else if (gearPosition === 'D' && dGearSpeedLimitRef.current !== null) {
        changeSpeedLimit(dGearSpeedLimitRef.current, false);
      }
    } else {
      // 退出接管时，恢复默认值
      getUserConfig();
      dGearSpeedLimitRef.current = null;
    }
  }, [takeOverBySelf, gearPosition, drivingModeKey]);

  useEffect(() => {
    const cb1 = () => {
      if (takeOverBySelf) {
        return;
      }
      getUserConfig();
    };
    const cb2 = (v: any) => {
      setCurSpeedLimit(v);
    };
    // 修改用户的接管设置后，在没有接管的情况下，需要更新下最大限速值
    addGlobalEventListener(EventName.CHANGE_USER_CONFIG, cb1);
    addGlobalEventListener('set-velocity-control', cb2);
    return () => {
      removeGlobalEventListener(EventName.CHANGE_USER_CONFIG, cb1);
      removeGlobalEventListener('set-velocity-control', cb2);
    };
  }, [takeOverBySelf]);

  useEffect(() => {
    if (gearPosition === 'R' && takeOverBySelf) {
      return;
    }
    if (
      takeOverBySelf &&
      curSpeedLimit !== enumFindNumber.get(speedLimitEnum.NO_Limit)
    ) {
      if (LC_BUTTON_TRIANGLE) {
        // 递增
        const nextSpeedLimit = getNextHigherSpeedLimit(curSpeedLimit);
        changeSpeedLimit(nextSpeedLimit);
        analyzeLog('change_speed_limit', {
          speedLimit: nextSpeedLimit,
        });
      } else if (LC_BUTTON_CROSS) {
        // 递减
        const nextSpeedLimit = getNextLowerSpeedLimit(curSpeedLimit);
        changeSpeedLimit(nextSpeedLimit);
        analyzeLog('change_speed_limit', {
          speedLimit: nextSpeedLimit,
        });
      }
    }
  }, [LC_BUTTON_TRIANGLE, LC_BUTTON_CROSS]);

  const getUserConfig = () => {
    request({
      ...cloudFetchConfig('/monitor/web/user/getUserDriveConfig'),
      method: Method.POST,
      body: {
        userName: props.userName,
      },
    }).then((res) => {
      if (res.code === HttpStatusCode.Success) {
        setInitSpeed(toNumericSpeedLimit(res?.data?.speedLimit) || 15);
      }
    });
  };

  const getImgUrl = (v: any) => {
    if (curSpeedLimit === speedLimitEnum.NO_Limit) {
      return v.active;
    }
    return v.num <= curSpeedLimit ? v.active : v.default;
  };

  const changeSpeedLimit = debounceImmediate(
    (val: number, showMessage: boolean = true) => {
      if (!props.takeOverBySelf) {
        message.error('用户未接管车辆,不允许操作!');
        return;
      }
      if (
        drivingModeKey ===
          DriveModeKey.DRIVEMODE_TAKEOVER_REMOTE_JOYSTICK_SUPER_CTRL &&
        takeOverBySelf &&
        gearPosition !== 'R' &&
        val > 10
      ) {
        message.error('强遥模式限速最高为10km/h');
        return;
      }
      if (
        drivingModeKey ===
          DriveModeKey.DRIVEMODE_TAKEOVER_REMOTE_JOYSTICK_SUPER_CTRL &&
        takeOverBySelf &&
        gearPosition === 'R' &&
        val > 5
      ) {
        message.error('R挡强遥模式限速最高为5km/h');
        return;
      }
      request({
        path: '/monitor/web/drive/command/send/velocity-control',
        method: Method.POST,
        body: {
          vehicleName: vehicleName,
          cockpitNumber: cockpitNumber,
          cockpitUserName: userName,
          data: Math.floor((val / 3.6) * 100) / 100,
        },
      }).then((res) => {
        if (res.code === HttpStatusCode.Success) {
          if (gearPosition !== 'R') {
            setCurSpeedLimit(val);
            if (gearPosition === 'D') {
              dGearSpeedLimitRef.current = val;
            }
          } else {
            if (dGearSpeedLimitRef.current === null) {
              dGearSpeedLimitRef.current = initSpeed;
            }
          }
          showMessage && message.success('指令下发成功');
        } else {
          showMessage && message.error(res?.message || '指令下发失败');
        }
      });
    },
    1000
  );

  return (
    <div className="speed-setting">
      <div
        className={`no-limit ${
          curSpeedLimit === enumFindNumber.get(speedLimitEnum.NO_Limit)
            ? 'active'
            : 'default'
        }`}
        onClick={() => {
          if (gearPosition === 'R' && takeOverBySelf) {
            return;
          }
          changeSpeedLimit(enumFindNumber.get(speedLimitEnum.NO_Limit)!);
        }}
      >
        不限制
      </div>
      <div className="speed-num">
        <div className="speed">
          {!curSpeedLimit ||
          curSpeedLimit === enumFindNumber.get(speedLimitEnum.NO_Limit)
            ? '-'
            : curSpeedLimit}
        </div>
        <div className="unit">km/h</div>
      </div>
      {speedLimitInfo.map((v, i) => {
        return (
          <img
            clstag={v.clstag}
            src={getImgUrl(v)}
            className="speed-limit"
            key={v.type}
            onClick={() => {
              if (gearPosition === 'R' && takeOverBySelf) {
                return;
              }
              changeSpeedLimit(v.num);
            }}
          />
        );
      })}
    </div>
  );
};

export default React.memo(SpeedControl);
